import {
  createElement,
  ElementType,
  FC,
  HTMLElementType,
  PropsWithChildren,
} from "react";

import { cn } from "@/lib/utils";

type Size = "sm" | "md" | "lg" | "xl";

type Props = PropsWithChildren<{
  title: string;
  description?: string;
  size?: Size;
  className?: string;
  separator?: boolean;
  icon?: ElementType;
}>;

const titleStyles: Record<Size, string> = {
  sm: "text-lg/tight",
  md: "text-xl/tight",
  lg: "text-2xl/tight",
  xl: "text-3xl/tight",
};

const iconStyles: Record<Size, string> = {
  sm: "w-5 h-5",
  md: "w-6 h-6",
  lg: "w-7 h-7",
  xl: "w-8 h-8",
};

const descriptionStyles: Record<Size, string> = {
  sm: "text-sm mt-0.5",
  md: "text-md mt-1",
  lg: "text-md mt-2",
  xl: "text-md mt-2",
};

const separatorStyles: Record<Size, string> = {
  sm: "pb-2",
  md: "pb-2",
  lg: "pb-2",
  xl: "pb-2",
};

const headerTypes: Record<Size, HTMLElementType> = {
  sm: "h4",
  md: "h3",
  lg: "h2",
  xl: "h1",
};

export const Header: FC<Props> = ({
  title,
  description,
  icon,
  size = "md",
  separator,
  children,
}) => {
  let glyph;

  if (icon) {
    glyph = createElement(icon, {
      className: cn("inline", iconStyles[size]),
      stroke: "var(--foreground)",
      strokeWidth: 2,
    });
  }

  // The header should be created semantically
  const header = createElement(headerTypes[size], {
    className: cn("font-bold justify-center", titleStyles[size]),
    children: [glyph && <span className="mr-2">{glyph}</span>, title],
  });

  const border = !separator
    ? ""
    : [separatorStyles[size], "border-b", "border-b-border"].join(" ");

  return (
    <div className={cn("flex justify-between w-full", border)}>
      <div className="flex-row">
        {header}
        {description && (
          <div className={cn("text-muted-foreground", descriptionStyles[size])}>
            {description}
          </div>
        )}
      </div>
      <div className="content-center">{children}</div>
    </div>
  );
};
